import React, {Component} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {FormattedMessage, injectIntl} from 'react-intl';
import type {WrappedComponentProps} from 'react-intl';
import Util from '../../forecast-app/shared/util/util';
import {
	BUDGET_TYPE,
	BUTTON_COLOR,
	type ButtonColor,
	type ButtonStyle,
	BUTTON_STYLE,
	HIDDEN_FEATURES,
	MODULE_TYPES,
	PERIOD_BUDGET_TYPE,
	PERIODICITY,
	TASK_COST_CALCULATION_TYPE,
} from '../../constants';
import CreateProjectMutation from '../../mutations/create_project_mutation';
import CreateRetainerPeriodMutation from '../../mutations/create_retainer_period_mutation';
import DuplicateProjectMutation from '../../mutations/duplicate_project_mutation';
import CreateProjectPersonMutation from '../../mutations/create_project_person_mutation';
import {withRouter, type RouteComponentProps} from 'react-router-dom';
import GenericModal from './generic_modal';
import Person from '../../forecast-app/shared/components/person/person';
import {MODAL_TYPE, showModal} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import {createProject, duplicateProject} from '../../components/scheduling/scheduling_mutations';
import moment from 'moment';
import type {Moment} from 'moment';
import {Scrollbars} from 'react-custom-scrollbars';
import NestedDropdown from '../../forecast-app/shared/components/dropdowns/nested_dropdown';
import AddTeamMemberModalContent from './add_team_member_modal_content';
import NewProjectModalDetailedSettings from '../../components/new-ui/new_project_modal_detailed_settings';
import projectUtil from '../../forecast-app/shared/util/ProjectUtil';
import Info from '../../components/info';
import Nova02Image from '../../images/graphics/Nova02Image';
import * as tracking from '../../tracking';
import AssignedDropdown from '../../forecast-app/shared/components/dropdowns/assigned-dropdown/assigned_dropdown';
import {hasFeatureFlag, isClientContactsFeatureEnabled, usingTimeApproval} from '../../forecast-app/shared/util/FeatureUtil';
import UpdateProjectMutation from '../../mutations/update_project_mutation.modern';
import {hasPermission, personIsClientUser, personIsVirtualUser} from '../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE, type PermissionType} from '../../Permissions';
import {hasModule} from '../../forecast-app/shared/util/ModuleUtil';
import {ClientPrimaryContactDropdown} from '../../forecast-app/shared/components/dropdowns/client-primary-contact-dropdown/ClientPrimaryContactDropdown';
import {FlexColumn, Label} from '@forecast-it/design-system';
import {trackEvent} from '../../tracking/amplitude/TrackingV2';
import {profilePicSrc, projectUrl} from '../../directApi';
import CompanySetupUtil, {isFeatureHidden} from '../../forecast-app/shared/util/CompanySetupUtil';
import {ProjectMainInfo} from './create-new-project/ProjectMainInfo';
import ValidateProjectIdMutation from '../../mutations/ValidateProjectIdMutation';
import SageUtil from '../../forecast-app/shared/util/sage/SageUtil';
import type {newProjectModalV2_viewer$data} from './__generated__/newProjectModalV2_viewer.graphql';
import type {PermissionsGroup} from '../settings/types';

type Viewer = newProjectModalV2_viewer$data;
type Company = NonNullable<Viewer['company']>;
type EdgeOf<C> = NonNullable<C> extends {readonly edges?: ReadonlyArray<infer E> | null} ? NonNullable<E> : never;
type NodeOf<C> = EdgeOf<C> extends {readonly node?: infer N} ? NonNullable<N> : never;
/** A connection whose edges and nodes are all present. */
type NodesPresent<N> = {readonly edges: ReadonlyArray<{readonly node: N}>};

/** The person fields the modal reads, for the team and project-owner pickers. */
type PersonNode = Pick<
	NodeOf<Company['allPersons']>,
	'id' | 'fullName' | 'firstName' | 'lastName' | 'role' | 'permissions' | 'client' | 'profilePictureId'
>;
type TeamNode = Pick<NodeOf<Company['teams']>, 'id' | 'name'> & {
	readonly teamPersons: {readonly edges: ReadonlyArray<{readonly node: {readonly person: {readonly id: string}}}>};
};
type ModalProject = {
	readonly id: string;
	readonly projectGroupId: string | null;
	readonly projectPersons: {readonly edges: ReadonlyArray<{readonly node: {readonly person: {readonly id: string}}}>};
};

/**
 * The parts of the viewer fragment the modal reads, with the connections it walks unconditionally
 * marked present: the query always returns them for a signed-in viewer's company.
 */
type ModalViewer = Pick<Viewer, 'actualPersonId' | 'availableFeatureFlags'> & {
	readonly project?: ModalProject | null;
	readonly company: Pick<
		Company,
		| 'id'
		| 'modules'
		| 'currency'
		| 'createdAt'
		| 'tier'
		| 'isUsingProjectAllocation'
		| 'isUsingMixedAllocation'
		| 'useTimeApproval'
		| 'projectLastSequence'
		| 'sageLocationEntities'
		| 'sageIntacctConnectionEnabled'
		| 'defaultRateCard'
	> & {
		readonly labels: NodesPresent<NodeOf<Company['labels']>>;
		readonly clients: NodesPresent<NodeOf<Company['clients']>>;
		readonly allPersons: NodesPresent<PersonNode>;
		readonly teams: NodesPresent<TeamNode>;
		readonly rateCards: NodesPresent<NodeOf<Company['rateCards']>>;
	};
};

type ProjectToDuplicate = {readonly id: string};
type ProjectNodeResult = {
	readonly id: string;
	readonly companyProjectId: number | null;
	readonly customProjectId: string | null;
};
type DuplicateResult = {
	readonly duplicateProject: {
		readonly errors?: unknown;
		readonly project: {readonly node: ProjectNodeResult & {readonly useManualAllocations?: boolean | null}};
	};
};
type CreateResult = {readonly createProject: {readonly errors?: unknown; readonly project: {readonly node: ProjectNodeResult}}};

type NewProjectModalProps = WrappedComponentProps &
	RouteComponentProps & {
		viewer: ModalViewer;
		closeModal: () => void;
		projectIdToAddPeople?: string | null;
		projectToDuplicate?: ProjectToDuplicate | null;
		projectToDuplicateId?: string | null;
		onCreateCallback?: (project: ProjectNodeResult, closeModal: () => void) => void;
		projectId?: string;
		projectGroupId?: string;
		buttonLocation?: string;
		isProjectLocked?: boolean;
		initialName?: string;
		initialBudgetType?: string;
		initialFixedPriceBudget?: number;
		initialStartDate?: string;
		initialEndDate?: string;
		initialRateCardName?: string | null;
		initialNoRateCard?: boolean;
	};

type RateCardOption = ReturnType<typeof projectUtil.resolveInitialRateCard>;

type NewProjectModalState = {
	projectName: string;
	customProjectId: string | undefined;
	customProjectIdError?: string;
	projectStartDate: Moment | null;
	projectEndDate: Moment | null;
	projectLabels: string[];
	useSprints: boolean;
	useBaseline: boolean;
	useDuplication: boolean;
	sprintLengthInDays: string | number;
	projectToDuplicate: ProjectToDuplicate | null;
	duplicateResult: DuplicateResult | null;
	addAutoScheduleStep: boolean;
	duplicateSettings: boolean;
	duplicateTasks: boolean;
	duplicatePhases: boolean;
	duplicateTeam: boolean;
	duplicateAllocations: boolean;
	duplicatePlaceholders: boolean;
	duplicateExpenses: boolean;
	duplicateDeliverables: boolean;
	duplicateRetainerPeriods: boolean;
	duplicateColor: boolean;
	useManualAllocations: boolean | null;
	budgetType: string;
	defaultPeriodPriceAmount: number | null;
	defaultPeriodHoursAmount: number | null;
	defaultPeriodBudgetType: string;
	defaultPeriodLength: number;
	wrongPeriodLength: boolean;
	wrongPeriodAmount: boolean;
	defaultPeriodPeriodicity: string;
	preCreatePeriods: boolean;
	preCreateAmount: number | '';
	periodStartDate: Moment;
	periodStartDateHovered: Moment | null;
	client: string | null;
	clientContact: {readonly id: string; readonly name: string} | null;
	locationEntityId: string | null;
	locationEntityError: string | undefined;
	fixedPriceBudget: number | string | null;
	projectToAddPeople: ModalProject | null | undefined;
	addProjectPeopleMode: boolean;
	addTeamMembersStep: boolean;
	newProjectId: string | undefined;
	newProjectCompanyProjectId?: number | null;
	newProjectCustomProjectId?: string | null;
	selectedTeamMembers: PersonNode[];
	selectedContactPersons: PersonNode[];
	errorMessage: string;
	missingProjectName: boolean;
	missingProjectToDuplicate: boolean;
	projectColor: string;
	settingsExpanded: boolean;
	rateCardSelected: RateCardOption;
	invalidFixedPriceFormat: boolean;
	createProjectPersonRequestSent: boolean;
	creatingProject: boolean;
	isSageIntacctEnabled: boolean;
	showInviteTeamMemberForm?: boolean;
};
type PeriodMutationInput = {
	name: string;
	projectId: string;
	startDay?: number;
	startMonth?: number;
	startYear?: number;
	preCreateAmount?: number | '';
};
type ProjectMutationInput = {
	name: string;
	customProjectId: string | undefined;
	labels: string[] | undefined;
	projectColor: string | undefined;
	rateCardId: unknown;
	projectStartDay?: number;
	projectStartMonth?: number;
	projectStartYear?: number;
	projectEndDay?: number;
	projectEndMonth?: number;
	projectEndYear?: number;
	clientId?: string;
	clientContactId?: string;
	sageIntacctLocationEntityId?: string;
	useManualAllocations?: boolean | null;
	projectId?: string;
	duplicateSettings?: boolean;
	duplicatePhases?: boolean;
	duplicateTasks?: boolean;
	duplicateProjectPersons?: boolean;
	duplicateAllocations?: boolean;
	duplicateExpenses?: boolean;
	duplicateDeliverables?: boolean;
	duplicateRetainerPeriods?: boolean;
	duplicateColor?: boolean;
	duplicatePlaceholders?: boolean;
	duplicatePlaceholderAllocations?: boolean;
	csrfToken?: unknown;
	socketClientId?: unknown;
	useBaseline?: boolean;
	budgetType?: string;
	defaultPeriodPriceAmount?: number;
	defaultPeriodHoursAmount?: number;
	defaultPeriodBudgetType?: string;
	defaultPeriodLength?: number;
	defaultPeriodPeriodicity?: string;
	budget?: number;
	sprintTimeBox?: boolean;
	sprintLengthInDays?: string | number;
};
type ModalButton = {
	text: string;
	cy?: string;
	className?: string;
	callback: () => void;
	style: ButtonStyle;
	color: ButtonColor;
	disabled?: boolean;
	preventDefaultClose?: boolean;
};
type BooleanStateKey = {
	[K in keyof NewProjectModalState]-?: NewProjectModalState[K] extends boolean ? K : never;
}[keyof NewProjectModalState];

/** PermissionsUtil types the permission list as PermissionType[]; Relay types it as nullable strings. */
const permissionsGroup = (person: PersonNode): PermissionsGroup => ({permissions: person.permissions as PermissionType[]});

// Normalises AI-supplied budget type strings (natural-language or enum) to BUDGET_TYPE constants.
const AI_BUDGET_TYPE_MAP = {
	fixed: BUDGET_TYPE.FIXED_PRICE,
	'fixed price': BUDGET_TYPE.FIXED_PRICE,
	fixed_price: BUDGET_TYPE.FIXED_PRICE,
	[BUDGET_TYPE.FIXED_PRICE]: BUDGET_TYPE.FIXED_PRICE,
	'time and materials': BUDGET_TYPE.TIME_AND_MATERIALS,
	't&m': BUDGET_TYPE.TIME_AND_MATERIALS,
	time_and_materials: BUDGET_TYPE.TIME_AND_MATERIALS,
	[BUDGET_TYPE.TIME_AND_MATERIALS]: BUDGET_TYPE.TIME_AND_MATERIALS,
	'non-billable': BUDGET_TYPE.NON_BILLABLE,
	'non billable': BUDGET_TYPE.NON_BILLABLE,
	non_billable: BUDGET_TYPE.NON_BILLABLE,
	[BUDGET_TYPE.NON_BILLABLE]: BUDGET_TYPE.NON_BILLABLE,
	retainer: BUDGET_TYPE.RETAINER,
	[BUDGET_TYPE.RETAINER]: BUDGET_TYPE.RETAINER,
};

class newProjectModalV2 extends Component<NewProjectModalProps, NewProjectModalState> {
	constructor(props: NewProjectModalProps) {
		super(props);

		// Only ever read for truthiness, where null and false agree.
		const useTimeApproval = usingTimeApproval(props.viewer.company.useTimeApproval ?? false);
		const actualPerson =
			useTimeApproval && props.viewer.company.allPersons.edges.find(p => p.node.id === props.viewer.actualPersonId);

		let projectToAddPeople: ModalProject | null | undefined = undefined;
		if (props.projectIdToAddPeople != null) {
			projectToAddPeople = props.viewer.project;
		}

		let selectedTeamMembers: PersonNode[] = [];
		if (projectToAddPeople) {
			const projectPersonIds = projectToAddPeople.projectPersons.edges.map(edge => edge.node.person.id);
			selectedTeamMembers = props.viewer.company.allPersons.edges
				.filter(edge => projectPersonIds.includes(edge.node.id))
				.map(edge => edge.node);
		} else if (actualPerson) {
			selectedTeamMembers.push(actualPerson.node);
		}
		const resolvedBudgetType = props.initialBudgetType
			? AI_BUDGET_TYPE_MAP[props.initialBudgetType.toLowerCase()] ?? null
			: null;
		const initialBudgetType =
			resolvedBudgetType ??
			(isFeatureHidden(HIDDEN_FEATURES.BUDGET_TYPES) ? BUDGET_TYPE.NON_BILLABLE : BUDGET_TYPE.TIME_AND_MATERIALS);
		// A pre-selected card is only honoured where the user can see and change it, so expanding the
		// section is worth doing only in the same case. See ProjectUtil.resolveInitialRateCard.
		const showsPreselectedRateCard =
			!!(props.initialRateCardName || props.initialNoRateCard) && projectUtil.isRateCardPickerVisible(initialBudgetType);

		const parsedStartDate = props.initialStartDate ? moment(props.initialStartDate) : null;
		const parsedEndDate = props.initialEndDate ? moment(props.initialEndDate) : null;

		this.state = {
			projectName: props.initialName || '',
			customProjectId: undefined,
			projectStartDate: parsedStartDate?.isValid() ? parsedStartDate : moment(),
			projectEndDate: parsedEndDate?.isValid() ? parsedEndDate : null,
			projectLabels: [],
			useSprints: false,
			useBaseline: false,
			useDuplication: false,
			sprintLengthInDays: '',
			projectToDuplicate: null,
			duplicateResult: null,
			addAutoScheduleStep: false,
			duplicateSettings: true,
			duplicateTasks: true,
			duplicatePhases: true,
			duplicateTeam: true,
			duplicateAllocations: true,
			duplicatePlaceholders: true,
			duplicateExpenses: CompanySetupUtil.hasFinance(), // Set to false if company does not have financial module
			duplicateDeliverables: true,
			duplicateRetainerPeriods: CompanySetupUtil.hasFinance(), // Set to false if company does not have financial module
			duplicateColor: true,
			useManualAllocations: props.viewer.company.isUsingProjectAllocation,
			budgetType: initialBudgetType,
			defaultPeriodPriceAmount: null,
			defaultPeriodHoursAmount: null,
			defaultPeriodBudgetType: PERIOD_BUDGET_TYPE.FIXED_HOURS,
			defaultPeriodLength: 1,
			wrongPeriodLength: false,
			wrongPeriodAmount: false,
			defaultPeriodPeriodicity: PERIODICITY.MONTHLY,
			preCreatePeriods: false,
			preCreateAmount: 1,
			periodStartDate: moment(),
			periodStartDateHovered: null,
			client: null,
			clientContact: null,
			locationEntityId: null,
			locationEntityError: undefined,
			// Only a fixed price project has a price field, so an amount sent with any other type is
			// dropped rather than kept in state where the create mutation would never read it.
			fixedPriceBudget:
				initialBudgetType === BUDGET_TYPE.FIXED_PRICE && props.initialFixedPriceBudget != null
					? props.initialFixedPriceBudget
					: null,
			projectToAddPeople,
			addProjectPeopleMode: !!projectToAddPeople,
			addTeamMembersStep: !!projectToAddPeople,
			newProjectId: projectToAddPeople?.id,
			selectedTeamMembers,
			selectedContactPersons: actualPerson ? [actualPerson.node] : [],
			errorMessage: '',
			missingProjectName: false,
			missingProjectToDuplicate: false,
			projectColor: '#bc97f5',
			// The rate card picker lives behind "more settings", so open that section when the caller
			// pre-selected a card the user will actually be shown there.
			settingsExpanded: showsPreselectedRateCard,
			rateCardSelected: this.getInitialRateCard(initialBudgetType),
			invalidFixedPriceFormat: false,
			createProjectPersonRequestSent: false,
			creatingProject: false,
			isSageIntacctEnabled: SageUtil.isSageIntacctEnabled(this.props.viewer.company),
		};
	}

	componentDidMount() {
		if (this.props.projectToDuplicate != null) {
			this.setState({useDuplication: true, projectToDuplicate: this.props.projectToDuplicate, settingsExpanded: true});
		} else if (this.props.projectToDuplicateId != null) {
			const duplicatedProject = {id: this.props.projectToDuplicateId};
			this.setState({useDuplication: true, projectToDuplicate: duplicatedProject, settingsExpanded: true});
		}
	}

	componentDidUpdate(_prevProps: NewProjectModalProps, prevState: NewProjectModalState) {
		if (
			prevState.periodStartDate !== this.state.periodStartDate ||
			prevState.preCreateAmount !== this.state.preCreateAmount ||
			prevState.defaultPeriodLength !== this.state.defaultPeriodLength ||
			prevState.defaultPeriodPeriodicity !== this.state.defaultPeriodPeriodicity
		) {
			const lastPeriodEndDate =
				this.state.projectStartDate &&
				this.state.preCreatePeriods &&
				this.state.preCreateAmount &&
				this.state.defaultPeriodLength
					? this.state.periodStartDateHovered
						? this.state.periodStartDateHovered
								.clone()
								.add(
									this.state.defaultPeriodLength * this.state.preCreateAmount,
									this.state.defaultPeriodPeriodicity === PERIODICITY.DAILY
										? 'days'
										: this.state.defaultPeriodPeriodicity === PERIODICITY.WEEKLY
										? 'weeks'
										: 'months'
								)
								.subtract(1, 'days')
						: this.state.periodStartDate
								.clone()
								.add(
									this.state.defaultPeriodLength * this.state.preCreateAmount,
									this.state.defaultPeriodPeriodicity === PERIODICITY.DAILY
										? 'days'
										: this.state.defaultPeriodPeriodicity === PERIODICITY.WEEKLY
										? 'weeks'
										: 'months'
								)
								.subtract(1, 'days')
					: null;
			if (this.state.preCreateAmount <= 0) {
				this.setState({projectEndDate: null});
			} else if (lastPeriodEndDate && lastPeriodEndDate !== this.state.projectEndDate) {
				this.setState({projectEndDate: lastPeriodEndDate});
			}
		}
	}

	newProjectModal: {scrollableContainer: {scrollToTop: () => void}} | null = null;

	handlePeriodLengthChange(
		newValues: Pick<NewProjectModalState, 'defaultPeriodLength' | 'wrongPeriodLength'> & {errorMessage?: string}
	) {
		// setState merges, so a missing errorMessage is left as it was; React's types want it named.
		this.setState(newValues as Pick<NewProjectModalState, 'defaultPeriodLength' | 'wrongPeriodLength' | 'errorMessage'>);
	}

	handledefaultPeriodChange(field: 'defaultPeriodHoursAmount' | 'defaultPeriodPriceAmount', value: number | null) {
		// isNaN already coerced its argument; Number() spells that out.
		this.setState({[field]: value, wrongPeriodAmount: !value || isNaN(Number(value))} as Pick<
			NewProjectModalState,
			typeof field | 'wrongPeriodAmount'
		>);
	}

	handleNumberPeriodChange(value: number | null) {
		this.setState({preCreateAmount: value ? Math.round(value) : ''});
	}

	onNumberPeriodChangeBlur() {
		if (this.state.preCreateAmount < 1) this.setState({preCreateAmount: 1});
	}

	handleInputChange(field: 'projectName' | 'customProjectId', value: string) {
		this.setState({[field]: value} as Pick<NewProjectModalState, typeof field>);
	}

	onCustomProjectIdChange(value: string) {
		this.handleInputChange('customProjectId', value);
		this.setState({customProjectIdError: undefined});
	}

	onCustomProjectIdBlur() {
		if (this.state.customProjectId == null || this.state.customProjectId === '') {
			this.setState({customProjectIdError: undefined});
			return;
		}
		if (!this.state.customProjectId.match(/^[a-z0-9-]+$/i)) {
			this.setState({
				customProjectIdError: this.props.intl.formatMessage({id: 'new_project_modal.project_id_error.alphanumeric'}),
			});
			return;
		}
		Util.CommitMutation(ValidateProjectIdMutation, {projectId: this.state.customProjectId}, result => {
			if (result.validateProjectId.valid === 'true') {
				trackEvent('Custom Project Id', 'Changed', {value: this.state.customProjectId});
				this.setState({customProjectIdError: undefined});
			} else {
				this.setState({
					customProjectIdError: this.props.intl.formatMessage({
						id: 'new_project_modal.project_id_error.already_in_use',
					}),
				});
			}
		});
	}

	handleCheckboxChange(value: boolean, field: BooleanStateKey) {
		this.setState({[field]: value} as Pick<NewProjectModalState, typeof field>);
	}

	handleDuplicateProjectSelectChange(selectedProject: ProjectToDuplicate | null) {
		if (selectedProject === null) {
			this.setState({projectToDuplicate: null, useDuplication: false, missingProjectToDuplicate: true});
		} else {
			this.setState({projectToDuplicate: selectedProject, useDuplication: true, missingProjectToDuplicate: false});
		}
	}

	updateLabels(labels: string[]) {
		this.setState({projectLabels: labels});
	}

	handleSettingsChange() {
		this.setState({duplicateSettings: !this.state.duplicateSettings});
	}

	handleTasksChange() {
		this.setState({duplicateTasks: !this.state.duplicateTasks});
	}

	handlePhasesChange() {
		this.setState({duplicatePhases: !this.state.duplicatePhases});
	}

	handleTeamChange() {
		this.setState({duplicateTeam: !this.state.duplicateTeam});
	}

	handleAllocationsChange() {
		this.setState({duplicateAllocations: !this.state.duplicateAllocations});
	}

	handlePlaceholdersChange() {
		this.setState({duplicatePlaceholders: !this.state.duplicatePlaceholders});
	}

	handleExpensesChange() {
		this.setState({duplicateExpenses: !this.state.duplicateExpenses});
	}

	handleDeliverablesChange() {
		this.setState({duplicateDeliverables: !this.state.duplicateDeliverables});
	}

	handleRetainerPeriodsChange() {
		this.setState({duplicateRetainerPeriods: !this.state.duplicateRetainerPeriods});
	}

	handleColorChange() {
		this.setState({duplicateColor: !this.state.duplicateColor});
	}

	handleFixedPriceBudget(fixedPriceBudget: number | string | null) {
		// parseFloat already coerced its argument; String() spells that out.
		this.setState({fixedPriceBudget, invalidFixedPriceFormat: !parseFloat(String(fixedPriceBudget))});
	}

	createNewLabel() {
		// The detailed settings require this prop but never call it.
	}

	handleSprintLengthChange(value: string) {
		this.setState({sprintLengthInDays: projectUtil.handleSprintLengthChange(value)});
	}

	createProject() {
		if (!this.state.wrongPeriodAmount) {
			if (
				!this.state.projectName ||
				this.state.projectName === '' ||
				(this.state.useDuplication && !this.state.projectToDuplicate)
			) {
				let missingName = false;
				if (!this.state.projectName || this.state.projectName === '') {
					this.newProjectModal?.scrollableContainer.scrollToTop();
					missingName = true;
				}
				const missingProject = this.state.useDuplication && !this.state.projectToDuplicate;

				this.setState({
					missingProjectName: missingName,
					missingProjectToDuplicate: missingProject,
					errorMessage: this.props.intl.formatMessage({id: 'common.required_field'}),
				});
			} else if (this.state.budgetType === BUDGET_TYPE.RETAINER && this.state.defaultPeriodLength < 1) {
				this.setState({
					wrongPeriodLength: true,
					errorMessage: this.props.intl.formatMessage({id: 'retainer.period_length_error'}),
				});
			} else if (
				this.state.budgetType === BUDGET_TYPE.RETAINER &&
				!(this.state.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.TIME_AND_MATERIALS) &&
				(!this.state.defaultPeriodHoursAmount || isNaN(this.state.defaultPeriodHoursAmount)) &&
				(!this.state.defaultPeriodPriceAmount || isNaN(this.state.defaultPeriodPriceAmount))
			) {
				this.setState({wrongPeriodAmount: true});
			} else if (
				((this.state.budgetType === BUDGET_TYPE.FIXED_PRICE || this.state.budgetType === BUDGET_TYPE.FIXED_PRICE_V2) &&
					!this.state.fixedPriceBudget) ||
				(this.state.fixedPriceBudget && !parseFloat(String(this.state.fixedPriceBudget)))
			) {
				this.setState({invalidFixedPriceFormat: true});
			} else if (this.state.customProjectIdError) {
				// An invalid custom project ID stops the create; its error is already showing.
			} else if (this.state.isSageIntacctEnabled && !this.state.locationEntityId) {
				this.setState({
					locationEntityError: this.props.intl.formatMessage({id: 'common.required_field'}),
				});
			} else if (!this.state.creatingProject) {
				this.setState({creatingProject: true});
				const onSuccess = (result: CreateResult) => {
					const errors = result.createProject.errors;
					if (Util.checkForSageErrorAndShowModal(errors, false)) {
						this.setState({creatingProject: false});
						return;
					}
					if (Util.isScheduling()) {
						Util.dispatchScheduleEvent(result);
					}
					if (this.props.onCreateCallback) {
						setTimeout(() => {
							/* Not the proudest of hacks, but since we are planning on sunsetting the project service I did not want to make it more complicated.
							 * We have a race condition:
							 *  - project is created in service company and a project create event is dispatched
							 *  - after project creation we directly invoke the project service to connect the project to the program
							 *  - the processing of the create event and the attachProjectToProgram call overlap and both
							 *    create a new project in the project service one of them will fail with a duplicate project error
							 *
							 *  we cannot create the project in the project service without creating it in service company (it needs a service company id)
							 *  we cannot attach the project in the project service event handler, since we need it to be done in a mutation so the graph is
							 *  updated.
							 *  If we could defer the event generation until after the project has been created and attached the problem would be solved.
							 *  But controlling the event order from the frontend is not very pretty either.
							 * */
							this.props.onCreateCallback?.(result.createProject.project.node, this.props.closeModal);
						}, 500);
					} else if (!this.state.useDuplication || !this.state.duplicateTeam) {
						this.setState({
							errorMessage: '',
							missingProjectName: false,
							missingProjectToDuplicate: false,
							addTeamMembersStep: true,
							newProjectId: result.createProject.project.node.id,
							newProjectCompanyProjectId: result.createProject.project.node.companyProjectId,
							newProjectCustomProjectId: result.createProject.project.node.customProjectId,
							invalidFixedPriceFormat: false,
						});
						if (this.state.budgetType === BUDGET_TYPE.RETAINER) {
							const startDate = this.state.periodStartDate;

							const periodMutationObject: PeriodMutationInput = {
								name: this.props.intl.formatMessage({id: 'retainer.new_period'}),
								projectId: result.createProject.project.node.id,
							};

							if (startDate) {
								periodMutationObject.startDay = startDate.date();
								periodMutationObject.startMonth = startDate.month() + 1;
								periodMutationObject.startYear = startDate.year();
							}

							if (this.state.preCreatePeriods) {
								periodMutationObject.preCreateAmount = this.state.preCreateAmount;
							}

							Util.CommitMutation(CreateRetainerPeriodMutation, periodMutationObject);
						}
					} else {
						if (!Util.isScheduling()) {
							if (this.state.budgetType === BUDGET_TYPE.RETAINER) {
								this.props.history.push(
									projectUrl(
										result.createProject.project.node.companyProjectId,
										result.createProject.project.node.customProjectId
									) + '/retainer-tracking'
								);
							} else {
								this.props.history.push(
									projectUrl(
										result.createProject.project.node.companyProjectId,
										result.createProject.project.node.customProjectId
									) + '/scoping'
								);
							}
						}
						this.props.closeModal();
					}
				};
				const onSuccessDuplicate = (result: DuplicateResult) => {
					const errors = result.duplicateProject.errors;
					if (Util.checkForSageErrorAndShowModal(errors, false)) {
						this.setState({creatingProject: false});
						return;
					}
					this.setState({
						duplicateResult: result,
					});

					if (
						hasFeatureFlag('auto_schedule_access') &&
						!result.duplicateProject.project.node.useManualAllocations &&
						hasPermission(PERMISSION_TYPE.PROJECTS_UPDATE)
					) {
						this.setState({
							addAutoScheduleStep: true,
						});
					} else {
						this.endProjectDuplication();
					}
				};
				const projectLabels: string[] = [];
				for (const label of this.state.projectLabels) {
					let isValid = true;
					try {
						atob(label).includes('Label');
					} catch (e) {
						isValid = false;
					}
					if (isValid && atob(label).includes('Label')) {
						projectLabels.push(label);
					}
				}
				const mutationObject: ProjectMutationInput = {
					name: this.state.projectName,
					customProjectId: this.state.customProjectId,
					labels: projectLabels,
					projectColor: this.state.useDuplication && this.state.duplicateColor ? undefined : this.state.projectColor,
					rateCardId: this.state.rateCardSelected.value,
				};
				if (this.state.projectStartDate) {
					mutationObject.projectStartDay = this.state.projectStartDate.date();
					mutationObject.projectStartMonth = this.state.projectStartDate.month() + 1;
					mutationObject.projectStartYear = this.state.projectStartDate.year();
				}
				if (this.state.projectEndDate) {
					mutationObject.projectEndDay = this.state.projectEndDate.date();
					mutationObject.projectEndMonth = this.state.projectEndDate.month() + 1;
					mutationObject.projectEndYear = this.state.projectEndDate.year();
				}
				if (this.state.client) {
					mutationObject.clientId = this.state.client;
				}
				// Carry the chosen Contact for both create and duplicate. The backend persists
				// it onto project_extension only when it is valid for the project's client.
				if (this.state.clientContact) {
					mutationObject.clientContactId = this.state.clientContact.id;
				}
				if (this.state.isSageIntacctEnabled && this.state.locationEntityId) {
					mutationObject.sageIntacctLocationEntityId = this.state.locationEntityId;
				}
				mutationObject.useManualAllocations = this.state.useManualAllocations;

				if (this.state.useDuplication) {
					// The checks above already stopped a duplicate with no project chosen.
					mutationObject.projectId = this.state.projectToDuplicate?.id;
					mutationObject.duplicateSettings = this.state.duplicateSettings;
					mutationObject.duplicatePhases = this.state.duplicatePhases;
					mutationObject.duplicateTasks = this.state.duplicateTasks;
					mutationObject.duplicateProjectPersons = this.state.duplicateTeam;
					mutationObject.duplicateAllocations = this.state.duplicateAllocations;
					mutationObject.duplicateExpenses = this.state.duplicateExpenses;
					mutationObject.duplicateDeliverables = this.state.duplicateDeliverables;
					mutationObject.duplicateRetainerPeriods = this.state.duplicateRetainerPeriods;
					mutationObject.duplicateColor = this.state.duplicateColor;
					mutationObject.useManualAllocations = undefined;

					if (hasModule(MODULE_TYPES.PLACEHOLDERS)) {
						mutationObject.duplicatePlaceholders = this.state.duplicatePlaceholders;
						mutationObject.duplicatePlaceholderAllocations = this.state.duplicatePlaceholders;
					}

					if (Util.isScheduling()) {
						mutationObject.csrfToken = Util.getCsrfValue();
						mutationObject.socketClientId = Util.getClientId();
						mutationObject.labels = undefined;
						mutationObject.rateCardId = undefined;
						duplicateProject(mutationObject, onSuccessDuplicate);
					} else {
						Util.CommitMutation(DuplicateProjectMutation, mutationObject, onSuccessDuplicate);
					}
				} else {
					// Set use baseline
					if (this.state.useBaseline && this.state.budgetType !== BUDGET_TYPE.RETAINER) {
						mutationObject.useBaseline = this.state.useBaseline;
					}

					mutationObject.budgetType = this.state.budgetType;
					if (this.state.budgetType === BUDGET_TYPE.RETAINER) {
						// Both the comparison and the unary plus already read null as 0.
						const priceAmount = this.state.defaultPeriodPriceAmount ?? 0;
						const hoursAmount = this.state.defaultPeriodHoursAmount ?? 0;
						mutationObject.defaultPeriodPriceAmount = priceAmount < 0 ? 0 : +priceAmount || 0;
						mutationObject.defaultPeriodHoursAmount = hoursAmount < 0 ? 0 : +hoursAmount || 0;
						mutationObject.defaultPeriodBudgetType = this.state.defaultPeriodBudgetType;
						mutationObject.defaultPeriodLength = this.state.defaultPeriodLength;
						mutationObject.defaultPeriodPeriodicity = this.state.defaultPeriodPeriodicity;
					}

					if (
						(this.state.budgetType === BUDGET_TYPE.FIXED_PRICE ||
							this.state.budgetType === BUDGET_TYPE.FIXED_PRICE_V2) &&
						this.state.fixedPriceBudget
					) {
						mutationObject.budget = parseFloat(String(this.state.fixedPriceBudget));
					}

					if (this.state.useSprints) {
						mutationObject.sprintTimeBox = this.state.useSprints;
						mutationObject.sprintLengthInDays = this.state.sprintLengthInDays ? this.state.sprintLengthInDays : 14;
					}

					if (Util.isScheduling()) {
						mutationObject.csrfToken = Util.getCsrfValue();
						mutationObject.socketClientId = Util.getClientId();
						createProject(mutationObject, onSuccess);
					} else {
						// creatingProject is set before committing and only ever cleared in onSuccess, so a
						// rejected create - an invalid rate card id, say - left the Create button disabled
						// for good behind the error modal, and the user had to reopen the modal to retry.
						// The shared onFailure returns true when it has already queued a retry, in which
						// case this attempt is not final and the button stays disabled for it.
						const onError = (transaction, onFailure) => {
							const retrying = onFailure(transaction);
							if (!retrying) {
								this.setState({creatingProject: false});
							}
						};
						Util.CommitMutation(CreateProjectMutation, mutationObject, onSuccess, true, null, onError);
					}
				}
			}
		}
	}

	navigateToNewProject() {
		this.props.closeModal();
		if (Util.isScheduling()) {
			return;
		}
		if (this.state.budgetType === BUDGET_TYPE.RETAINER) {
			this.props.history.push(
				projectUrl(this.state.newProjectCompanyProjectId, this.state.newProjectCustomProjectId) + '/retainer-tracking'
			);
		} else if (this.state.useBaseline && Util.hasBaselinePageAccess()) {
			this.props.history.push(
				projectUrl(this.state.newProjectCompanyProjectId, this.state.newProjectCustomProjectId) + '/baseline'
			);
		} else {
			this.props.history.push(
				projectUrl(this.state.newProjectCompanyProjectId, this.state.newProjectCustomProjectId) + '/scoping'
			);
		}
	}

	onClientSelect(selected: {value: string | null} | null) {
		// A contact is scoped to its client, so changing the client clears the chosen Contact.
		this.setState({client: selected ? selected.value : null, clientContact: null});
	}

	// Check if client Sage Intacct Location Entity ID matches the selected location entity
	onLocationEntitySelect(selected: {locationId?: string | null} | null) {
		const locationId = selected?.locationId ?? null;
		this.setState(prev => {
			const next: Pick<NewProjectModalState, 'locationEntityId' | 'locationEntityError'> = {
				locationEntityId: locationId,
				locationEntityError: undefined,
			};
			if (!prev.isSageIntacctEnabled || !prev.client || locationId == null) {
				return next;
			}
			const edges = this.props.viewer.company.clients.edges;
			const edge = edges.find(e => e.node.id === prev.client);
			const clientLocId = edge?.node?.sageIntacctLocationEntityId ?? null;
			return clientLocId !== locationId ? {...next, client: null} : next;
		});
	}

	onDateRangeChangeIntervalObject(interval: {startDate: string; endDate: string}) {
		this.setState({
			projectStartDate: moment(interval.startDate),
			projectEndDate: moment(interval.endDate),
		});
	}

	onProjectTypeChange(useManualAllocations: boolean) {
		if (useManualAllocations !== this.state.useManualAllocations) {
			this.setState({useManualAllocations: useManualAllocations});
		}
	}

	onBudgetTypeChange(newValues: Partial<NewProjectModalState> & {budgetType: string}) {
		// setState merges a partial update; React's types only take one whose keys are all present.
		this.setState(newValues as NewProjectModalState);
	}

	onContactSelect(personIds: string[]) {
		const selectedTeamMembers = this.state.selectedTeamMembers;
		const selectedTeamMembersIds = selectedTeamMembers.map(person => person.id);

		const selectedContactPersons = this.state.selectedContactPersons;
		const selectedContactPersonsIds = selectedContactPersons.map(person => person.id);

		const personId = personIds.length > 0 && personIds[0];
		// The ids come from options built from allPersons, so the find always succeeds.
		const person = personId ? this.props.viewer.company.allPersons.edges.find(p => p.node.id === personId) : undefined;

		if (personId && person) {
			if (!selectedContactPersonsIds.includes(personId)) {
				selectedContactPersons.push(person.node);
			}
			if (!selectedTeamMembersIds.includes(personId)) {
				selectedTeamMembers.push(person.node);
			}

			this.setState({selectedTeamMembers, selectedContactPersons});
		}
	}

	unassignContact(personId: string) {
		const selectedContactPersons = this.state.selectedContactPersons;
		const filteredAssigned = selectedContactPersons.filter(personEdge => personEdge.id !== personId);
		this.setState({selectedContactPersons: filteredAssigned});
	}

	onTeamSelect(selected: {person?: PersonNode; value?: string}) {
		const selectedTeamMembers = this.state.selectedTeamMembers;
		const selectedTeamMembersIds = selectedTeamMembers.map(person => person.id);
		if (selected.person) {
			if (!selectedTeamMembersIds.includes(selected.person.id)) {
				selectedTeamMembers.push(selected.person);
			}
		} else {
			const selectedTeam = this.props.viewer.company.teams.edges.find(team => team.node.id === selected.value);
			if (selectedTeam) {
				const selectedTemPersonIds = selectedTeam.node.teamPersons.edges.map(tp => tp.node.person.id);
				this.props.viewer.company.allPersons.edges
					.filter(person => !personIsClientUser(permissionsGroup(person.node)))
					.forEach(person => {
						if (selectedTemPersonIds.includes(person.node.id) && !selectedTeamMembersIds.includes(person.node.id)) {
							selectedTeamMembers.push(person.node);
						}
					});
			}
		}
		this.setState({selectedTeamMembers});
	}

	removeFromSelectedTeamMembers(person: PersonNode) {
		const selectedTeamMembers = this.state.selectedTeamMembers.filter(p => p.id !== person.id);
		const selectedContactPersons = this.state.selectedContactPersons.filter(p => p.id !== person.id);
		this.setState({selectedTeamMembers, selectedContactPersons});
	}

	addPeopleToProject() {
		const selectedTeamMembersIds = this.state.selectedTeamMembers.map(person => person.id);
		const selectedContactIds = this.state.selectedContactPersons.map(person => person.id);
		let projectId: string | null | undefined = this.state.newProjectId;
		let projectGroupId: string | null = null;
		const {projectToAddPeople} = this.state;
		// addProjectPeopleMode is set from projectToAddPeople, so the second check never differs.
		if (this.state.addProjectPeopleMode && projectToAddPeople) {
			const isConnectedProject = !!projectToAddPeople.projectGroupId;
			projectId = isConnectedProject ? null : projectToAddPeople.id;
			projectGroupId = isConnectedProject ? projectToAddPeople.projectGroupId : null;
		}
		const showAllocationModal = (personIds: string[]) => {
			// only if project allocations show allocations modal
			if (this.state.useManualAllocations || this.props.viewer.company.isUsingMixedAllocation) {
				showModal(
					{
						type: MODAL_TYPE.ALLOCATE_TEAM_MEMBERSV2,
						projectId,
						projectGroupId,
						selectedTeamMembersIds: personIds,
						closeModal: () => this.navigateToNewProject(),
					},
					true
				);
			} else {
				this.props.closeModal();

				if (!Util.isScheduling()) {
					this.navigateToNewProject();
				}
			}
		};

		if (!this.state.createProjectPersonRequestSent) {
			this.setState({createProjectPersonRequestSent: true}); // only allow sending a single createProjectPerson request from the modal to avoid duplicates

			if (this.state.addProjectPeopleMode) {
				// The query fetches the project whenever the modal is adding people to one.
				const originalSelectedProjectPersonIds =
					this.props.viewer.project?.projectPersons.edges.map(edge => edge.node.person.id) ?? [];
				const addedPersonIds = selectedTeamMembersIds.filter(
					personId => !originalSelectedProjectPersonIds.includes(personId)
				);
				if (addedPersonIds) {
					Util.CommitMutation(
						CreateProjectPersonMutation,
						{
							projectId,
							projectGroupId,
							personIds: addedPersonIds,
						},
						response => {
							Util.dispatchScheduleEvent(response);
							showAllocationModal(addedPersonIds);
						}
					);
				}
			} else if (selectedTeamMembersIds.length !== 0) {
				Util.CommitMutation(
					CreateProjectPersonMutation,
					{
						projectId: this.state.newProjectId,
						personIds: selectedTeamMembersIds,
						contactIds: selectedContactIds,
					},
					response => {
						if (Util.isScheduling()) {
							Util.dispatchScheduleEvent(response);
						}
						// Change to project cost calculation if using baseline and assigning anybody
						if (this.state.useBaseline) {
							Util.CommitMutation(
								UpdateProjectMutation,
								{
									id: this.state.newProjectId,
									taskCostCalculationType: TASK_COST_CALCULATION_TYPE.PROJECT_TEAM_AVERAGE_UNASSIGNED_TASKS,
								},
								() => showAllocationModal(selectedTeamMembersIds)
							);
						} else {
							showAllocationModal(selectedTeamMembersIds);
						}
					}
				);
				// Return because be want to wait until the CreateProjectPersonMutation is finished before navigating to the new project.
				return;
			} else {
				this.props.closeModal();
			}
		}
		if (Util.isScheduling()) return;
		this.navigateToNewProject();
	}

	onPeriodicityChange(periodicity: {value: string}) {
		this.setState({defaultPeriodPeriodicity: periodicity.value});
	}

	onPeriodBudgetTypeChange(periodBudgetType: string[]) {
		this.setState({defaultPeriodBudgetType: periodBudgetType[0]});
	}

	handleProjectNameBlur() {
		if (this.state.projectName && this.state.projectName !== '') {
			this.setState({errorMessage: '', missingProjectName: false});
		}
	}

	handleSelectColorChange(color: string) {
		this.setState({
			projectColor: color,
		});
	}

	toggleMoreSettings() {
		this.setState({
			settingsExpanded: !this.state.settingsExpanded,
		});
	}

	getDefaultRateCard() {
		const {formatMessage} = this.props.intl;
		return projectUtil.getDefaultRateCard(
			formatMessage,
			// getDefaultRateCard only tests it for truthiness, so null and undefined read the same.
			this.props.viewer.company.defaultRateCard ?? undefined,
			this.props.viewer.company.currency
		);
	}

	/**
	 * The rate card the modal opens on. Thin wrapper over ProjectUtil.resolveInitialRateCard, which
	 * holds the rules and is tested directly; this only unwraps the Relay connection for it.
	 *
	 * Called from the constructor, so it reads props rather than state - `budgetType` is the value
	 * being written into the initial state, not this.state.budgetType, which does not exist yet.
	 */
	getInitialRateCard(budgetType: string) {
		return projectUtil.resolveInitialRateCard({
			rateCards: this.props.viewer.company.rateCards.edges.map(edge => edge.node),
			budgetType,
			initialRateCardName: this.props.initialRateCardName,
			initialNoRateCard: this.props.initialNoRateCard,
			defaultRateCard: this.props.viewer.company.defaultRateCard ?? undefined,
			companyCurrency: this.props.viewer.company.currency,
			formatMessage: this.props.intl.formatMessage,
		});
	}

	handleSelectRateChange(rate: RateCardOption) {
		this.setState({
			rateCardSelected: rate,
		});
	}

	changePeriodStartDate(start: Moment) {
		this.setState({
			periodStartDate: start,
			projectStartDate: start,
			periodStartDateHovered: null,
		});
	}

	setHoveredDay(day: Moment | null) {
		this.setState({
			periodStartDateHovered: day,
		});
	}

	endProjectDuplication() {
		if (Util.isScheduling()) {
			Util.dispatchScheduleEvent(this.state.duplicateResult);
		} else {
			// Only reached once the duplicate has returned, so the result is always there.
			const project = this.state.duplicateResult?.duplicateProject.project.node;
			this.props.history.push(projectUrl(project?.companyProjectId, project?.customProjectId) + '/settings');
		}
		this.props.closeModal();
	}

	getNewProjectModalContent() {
		const {formatMessage} = this.props.intl;
		const options_clients: {label: string; value: string | null; cy: string; locationEntityId?: string | null}[] =
			this.props.viewer.company.clients.edges
				.map(client => ({
					value: client.node.id,
					label: client.node.name,
					locationEntityId: client.node.sageIntacctLocationEntityId ?? null,
					cy: `new-project-client-${client.node.id}`,
				}))
				.sort((a, b) => Util.sortAlphabetically(a.label, b.label));
		options_clients.splice(0, 0, {
			value: null,
			label: formatMessage({id: 'client_selector.no_client_text'}),
			cy: 'new-project-client-none',
		});
		const sageEntities = this.props.viewer.company.sageLocationEntities ?? [];
		const createdAt = moment(this.props.viewer.company.createdAt, 'x');
		const transitionDay = moment('2021-03-24'); //from this day people can choose a custom color in the project settings

		return (
			<div className="new-project-content-wrapper-v2" data-cy={'new-project-modal'}>
				<ProjectMainInfo
					label={formatMessage({id: 'common.project-name'})}
					onProjectNameChange={this.handleInputChange.bind(this, 'projectName')}
					onProjectNameBlur={this.handleProjectNameBlur.bind(this)}
					projectName={this.state.projectName}
					invalidProjectName={this.state.missingProjectName}
					projectNameErrorMessage={this.state.errorMessage}
					/**/
					customProjectId={this.state.customProjectId}
					onCustomProjectIdChange={this.onCustomProjectIdChange.bind(this)}
					onCustomProjectIdBlur={this.onCustomProjectIdBlur.bind(this)}
					customProjectIdError={this.state.customProjectIdError}
					/**/
					useDuplication={this.state.useDuplication}
					isProjectLocked={this.props.isProjectLocked}
					startDate={this.state.projectStartDate?.format('YYYY-MM-DD')}
					endDate={this.state.projectEndDate?.format('YYYY-MM-DD')}
					handleDateRangeChange={this.onDateRangeChangeIntervalObject.bind(this)}
					/**/
					clientOptions={options_clients}
					onClientChange={this.onClientSelect.bind(this)}
					client={this.state.client}
					/* Sage Intacct */
					locationEntityOptions={sageEntities}
					onLocationEntityChange={this.onLocationEntitySelect.bind(this)}
					locationEntity={this.state.locationEntityId}
					locationEntityError={this.state.locationEntityError}
					isSageIntacctEnabled={this.state.isSageIntacctEnabled}
					/**/
					duplicateColor={this.state.duplicateColor}
					projectColor={this.state.projectColor}
					onColorSelect={this.handleSelectColorChange.bind(this)}
					isOldCompany={createdAt.isBefore(transitionDay)}
					// The placeholder adds 1 to it, which already read null as 0.
					lastCompanyProjectId={this.props.viewer.company.projectLastSequence ?? 0}
				/>

				{this.state.client && isClientContactsFeatureEnabled() ? (
					// Half-width to line up with the Client/Dates fields above (matches their ~350px width).
					<div className="new-project-contact" data-cy={'new-project-contact'} style={{maxWidth: '350px'}}>
						{/* Render the label via FlexColumn/Label (no gap prop) so the label-to-field spacing
						    matches the Client/Dates fields above, rather than the dropdown's built-in label. */}
						<FlexColumn>
							<Label>{formatMessage({id: 'common.contact'})}</Label>
							<ClientPrimaryContactDropdown
								key={this.state.client}
								clientId={this.state.client}
								value={this.state.clientContact}
								onChange={contact => this.setState({clientContact: contact})}
								hideLabel
								placeholder={formatMessage({id: 'project_settings.select_contact'})}
								dataCy={'new-project-client-contact'}
							/>
						</FlexColumn>
					</div>
				) : null}

				<div className={'separation'}>
					<div className={'separation-line'} />
				</div>
				<div className={'more-settings'} data-cy={'more-settings'} onClick={this.toggleMoreSettings.bind(this)}>
					<div data-userpilot={'more-settings'}>
						{this.state.settingsExpanded
							? formatMessage({id: 'project_settings.fewer_settings'})
							: formatMessage({id: 'project_settings.more_settings'})}
					</div>
					<div className={'arrow' + (this.state.settingsExpanded ? ' expanded' : '')} />
				</div>

				{this.state.settingsExpanded ? (
					<NewProjectModalDetailedSettings
						modules={this.props.viewer.company.modules}
						useSprints={this.state.useSprints}
						useBaseline={this.state.useBaseline}
						projectToDuplicate={this.state.projectToDuplicate}
						missingProjectToDuplicate={this.state.missingProjectToDuplicate}
						errorMessage={this.state.errorMessage}
						sprintLengthInDays={this.state.sprintLengthInDays}
						handleSprintLengthChange={this.handleSprintLengthChange.bind(this)}
						useDuplication={this.state.useDuplication}
						labels={this.props.viewer.company.labels.edges.map(edge => edge.node)}
						handleCheckboxChange={this.handleCheckboxChange.bind(this)}
						handleSettingsChange={this.handleSettingsChange.bind(this)}
						handleTasksChange={this.handleTasksChange.bind(this)}
						handlePhasesChange={this.handlePhasesChange.bind(this)}
						handleTeamChange={this.handleTeamChange.bind(this)}
						handleAllocationsChange={this.handleAllocationsChange.bind(this)}
						handlePlaceholdersChange={this.handlePlaceholdersChange.bind(this)}
						handleExpensesChange={this.handleExpensesChange.bind(this)}
						handleDeliverablesChange={this.handleDeliverablesChange.bind(this)}
						handleRetainerPeriodsChange={this.handleRetainerPeriodsChange.bind(this)}
						handleColorChange={this.handleColorChange.bind(this)}
						duplicateSettings={this.state.duplicateSettings}
						duplicateTasks={this.state.duplicateTasks}
						duplicatePhases={this.state.duplicatePhases}
						duplicateTeam={this.state.duplicateTeam}
						duplicateAllocations={this.state.duplicateAllocations}
						duplicatePlaceholders={this.state.duplicatePlaceholders}
						duplicateExpenses={this.state.duplicateExpenses}
						duplicateDeliverables={this.state.duplicateDeliverables}
						duplicateRetainerPeriods={this.state.duplicateRetainerPeriods}
						duplicateColor={this.state.duplicateColor}
						useManualAllocations={this.state.useManualAllocations}
						onProjectTypeChange={this.onProjectTypeChange.bind(this)}
						budgetType={this.state.budgetType}
						onBudgetTypeChange={this.onBudgetTypeChange.bind(this)}
						handleFixedPriceBudget={this.handleFixedPriceBudget.bind(this)}
						invalidFixedPriceFormat={this.state.invalidFixedPriceFormat}
						rateCardSelected={this.state.rateCardSelected}
						companyCurrency={this.props.viewer.company.currency}
						fixedPriceBudget={this.state.fixedPriceBudget}
						handleSelectRateChange={this.handleSelectRateChange.bind(this)}
						rateCards={this.props.viewer.company.rateCards.edges}
						onPeriodicityChange={this.onPeriodicityChange.bind(this)}
						defaultPeriodPeriodicity={this.state.defaultPeriodPeriodicity}
						wrongPeriodLength={this.state.wrongPeriodLength}
						wrongPeriodAmount={this.state.wrongPeriodAmount}
						defaultPeriodLength={this.state.defaultPeriodLength}
						defaultPeriodBudgetType={this.state.defaultPeriodBudgetType}
						onPeriodBudgetTypeChange={this.onPeriodBudgetTypeChange.bind(this)}
						handlePeriodLengthChange={this.handlePeriodLengthChange.bind(this)}
						handledefaultPeriodChange={this.handledefaultPeriodChange.bind(this)}
						defaultPeriodHoursAmount={this.state.defaultPeriodHoursAmount}
						defaultPeriodPriceAmount={this.state.defaultPeriodPriceAmount}
						projectStartDate={this.state.projectStartDate}
						preCreatePeriods={this.state.preCreatePeriods}
						handleNumberPeriodChange={this.handleNumberPeriodChange.bind(this)}
						onNumberPeriodChangeBlur={this.onNumberPeriodChangeBlur.bind(this)}
						preCreateAmount={this.state.preCreateAmount}
						periodStartDate={this.state.periodStartDate}
						periodStartDateHovered={this.state.periodStartDateHovered}
						handleDuplicateProjectSelectChange={this.handleDuplicateProjectSelectChange.bind(this)}
						createNewLabel={this.createNewLabel.bind(this)}
						updateLabels={this.updateLabels.bind(this)}
						setHoveredDay={this.setHoveredDay.bind(this)}
						changePeriodStartDate={this.changePeriodStartDate.bind(this)}
						projectLabels={this.state.projectLabels}
						availableFeatureFlags={this.props.viewer.availableFeatureFlags}
						tier={this.props.viewer.company.tier}
						isUsingProjectAllocation={this.state.useManualAllocations}
						isUsingMixedAllocation={this.props.viewer.company.isUsingMixedAllocation}
					/>
				) : (
					<div className={'project-third-info empty'} />
				)}
			</div>
		);
	}

	personInvited(person: PersonNode) {
		this.state.selectedTeamMembers.push(person);
		this.setState({selectedTeamMembers: this.state.selectedTeamMembers});
	}

	// V3 chargebeee & seat based modal - THIS IS ONLY WITH CHARGEBEE PLANS
	getV3AddPeopleToProjectContent() {
		const {formatMessage} = this.props.intl;
		// Only ever read for truthiness, where null and false agree.
		const useTimeApproval = usingTimeApproval(this.props.viewer.company.useTimeApproval ?? false);
		const selectedTeamMembersIds = this.state.selectedTeamMembers.map(person => person.id);
		const selectedContactPersonsIds = this.state.selectedContactPersons.map(person => person.id);
		const team_member_options = this.props.viewer.company.allPersons.edges
			.filter(
				person => !selectedTeamMembersIds.includes(person.node.id) && !personIsClientUser(permissionsGroup(person.node))
			)
			.map(person => ({value: person.node.id, label: person.node.fullName, person: person.node}));
		const team_options = this.props.viewer.company.teams.edges
			.filter(
				team =>
					team.node.teamPersons.edges.filter(tp => !selectedTeamMembersIds.includes(tp.node.person.id)).length !== 0
			)
			.map(team => ({value: team.node.id, label: team.node.name}));

		const options = [
			{value: 'teams', label: formatMessage({id: 'settings_team.teams_section_title'}), nestedOptions: team_options},
			{
				value: 'team-members',
				isPersonSelector: true,
				label: formatMessage({id: 'settings.team-members'}),
				nestedOptions: team_member_options,
			},
		];

		const contact_options = this.props.viewer.company.allPersons.edges
			.filter(
				person =>
					!selectedContactPersonsIds.includes(person.node.id) &&
					!personIsClientUser(permissionsGroup(person.node)) &&
					!personIsVirtualUser(permissionsGroup(person.node))
			)
			.map(p => {
				return {node: {person: p.node}};
			});

		const selectedTeamMembers = this.selectedTeamMembersToShow();

		const addTeamMembersContent = (
			<div>
				<AddTeamMemberModalContent
					projectId={this.props.projectId}
					projectGroupId={this.props.projectGroupId}
					buttonLocation={this.props.buttonLocation}
					viewer={this.props.viewer}
					personInvited={this.personInvited.bind(this)}
					closeModal={this.props.closeModal}
				/>
			</div>
		);

		return (
			<div
				className={
					'add-team-modal-v3' +
					(useTimeApproval ? ' tall' : '') +
					(this.state.showInviteTeamMemberForm ? ' wide' : '')
				}
			>
				{this.state.showInviteTeamMemberForm ? null : (
					<div className="team-select">
						{useTimeApproval && !this.state.addProjectPeopleMode ? (
							<div className={'contact-dropdown-wrapper'}>
								<div className={'dropdown-header-text'}>{formatMessage({id: 'common.project_owners'})}</div>
								<AssignedDropdown
									cy={'add-contact-member'}
									assignedPersons={this.state.selectedContactPersons}
									assignablePersons={contact_options}
									assignPerson={personId => this.onContactSelect(personId)}
									unassignPerson={this.unassignContact.bind(this)}
									customPlaceholder={formatMessage({id: 'new_project_modal.select_project_owners'})}
									maxPeopleIconsShown={10}
								/>
							</div>
						) : null}
						<div className={'dropdown-header-text'}>
							{formatMessage({id: 'common.team'}) + '/' + formatMessage({id: 'settings.team-members'})}
						</div>
						<NestedDropdown
							cy={'add-team-member'}
							key={'team-dropdown'}
							options={options}
							onChange={this.onTeamSelect.bind(this)}
							clearable={false}
							placeholder={formatMessage({id: 'add_team_modal.select_team_people_placeholder'})}
							nested={true}
							stayOpenOnSelect={true}
							showArrowWhenCollapsed={true}
							maxDropdownHeight={302}
						/>
						{/** Check for PERSON_CREATE permission */}
						{hasPermission(PERMISSION_TYPE.PERSON_CREATE) ? (
							<div
								className={'invite-link-section'}
								onClick={() => this.setState({showInviteTeamMemberForm: true})}
							>
								<div className={'invite-text'}>
									<FormattedMessage
										id={'new_project_modal.invite_team_member'}
										values={{
											inviteLink: (
												<span className={'green'}>
													<FormattedMessage id={'new_project_modal.invite_team_member_link'} />
												</span>
											),
										}}
									/>
								</div>
							</div>
						) : null}
					</div>
				)}
				{this.state.showInviteTeamMemberForm ? <div className={'invite-section'}>{addTeamMembersContent}</div> : null}
				<span className="selected-count">
					{formatMessage({id: 'new_project_modal.add_team_members_count'}, {count: selectedTeamMembers.length})}
				</span>
				<div className="team-members-select">
					<Scrollbars autoHeight={true} autoHeightMin={1} autoHeightMax={259} hideTracksWhenNotNeeded={true}>
						<ul>
							{selectedTeamMembers
								? selectedTeamMembers.map((person, index) => (
										<li key={index}>
											<div
												className={
													'person-list-option' +
													(index === selectedTeamMembers.length - 1 ? ' last-option' : '')
												}
											>
												<div className="person-details-wrapper">
													<div style={{minWidth: '28px', maxWidth: '212px'}}>
														<Person
															name={`${person.firstName} ${person.lastName}`}
															showName={false}
															showRole={false}
															imageSrc={profilePicSrc(person.profilePictureId)}
															imageSize="new-ui-dropdown"
														/>
													</div>
													<div className="team-member-name-email">
														<span data-cy={'team-member-name'} className="team-member-name">
															{person.firstName + ' ' + person.lastName}
														</span>
														<span className="team-member-email">
															{person.role ? person.role.name : ''}
														</span>
													</div>
												</div>
												{this.getTeamForPerson(person) ? (
													<div className={'team-indicator'}>
														<span className={'team-indicator-text'}>
															{this.getTeamForPerson(person)?.node.name}
														</span>
													</div>
												) : (
													<div className={'team-indicator-placeholder'} />
												)}
												<button
													className="remove-selected-button"
													onClick={this.removeFromSelectedTeamMembers.bind(this, person)}
												/>
											</div>
										</li>
								  ))
								: null}
						</ul>
					</Scrollbars>
				</div>
			</div>
		);
	}

	selectedTeamMembersToShow() {
		let hiddenSelectedPersonIds: string[] = [];
		if (this.state.addProjectPeopleMode) {
			// The query fetches the project whenever the modal is adding people to one.
			hiddenSelectedPersonIds = this.props.viewer.project?.projectPersons.edges.map(edge => edge.node.person.id) ?? [];
		}
		return this.state.selectedTeamMembers.filter(person => !hiddenSelectedPersonIds.includes(person.id));
	}

	getAutoScheduleModal() {
		const {formatMessage} = this.props.intl;

		const multiInfoMessage: {title: string; text: string}[] = [];
		multiInfoMessage.push({
			title: formatMessage({id: 'new_project_modal.auto_schedule_info_tooltip_1_headline'}),
			text: formatMessage({id: 'new_project_modal.auto_schedule_info_tooltip_1_text'}),
		});
		multiInfoMessage.push({
			title: formatMessage({id: 'new_project_modal.auto_schedule_info_tooltip_2_headline'}),
			text: formatMessage({id: 'new_project_modal.auto_schedule_info_tooltip_2_text'}),
		});
		multiInfoMessage.push({
			title: formatMessage({id: 'new_project_modal.auto_schedule_info_tooltip_3_headline'}),
			text: formatMessage({id: 'new_project_modal.auto_schedule_info_tooltip_3_text'}),
		});
		multiInfoMessage.push({
			title: formatMessage({id: 'new_project_modal.auto_schedule_info_tooltip_4_headline'}),
			text: formatMessage({id: 'new_project_modal.auto_schedule_info_tooltip_4_text'}),
		});

		const content = (
			<div className="auto-scheduling-step-content">
				<div className="auto-scheduling-description">{formatMessage({id: 'new_project_modal.auto_schedule_text'})}</div>
				<div className="info-row">
					<div>{formatMessage({id: 'new_project_modal.auto_schedule_info'})}</div>
					<Info small={true} multiInfoMessage={multiInfoMessage} showOnTop wideToopltip />
				</div>
			</div>
		);
		return content;
	}

	getTeamForPerson(person: PersonNode) {
		const teams = this.props.viewer.company.teams.edges;
		const team = teams.find(team => team.node.teamPersons.edges.find(p => p.node.person.id === person.id));
		return team;
	}

	goBack() {
		this.setState({showInviteTeamMemberForm: false});
	}

	openAutoScheduling() {
		// The auto-schedule step only shows once the duplicate has returned, so the result is always there.
		const project = this.state.duplicateResult?.duplicateProject.project.node;
		this.props.history.push(`${projectUrl(project?.companyProjectId, project?.customProjectId)}/schedule#auto-schedule`);
	}

	render() {
		const {formatMessage} = this.props.intl;

		const datesValid =
			!this.state.projectStartDate ||
			!this.state.projectEndDate ||
			this.state.projectStartDate <= this.state.projectEndDate;
		const buttons: ModalButton[] = this.state.addTeamMembersStep
			? [
					this.state.addProjectPeopleMode
						? {
								text: formatMessage({id: 'common.cancel'}),
								callback: this.props.closeModal,
								style: BUTTON_STYLE.FILLED,
								color: BUTTON_COLOR.WHITE,
						  }
						: {
								text: formatMessage({id: 'project_allocation_modal.skip'}),
								cy: 'skip-allocation-button',
								callback: this.navigateToNewProject.bind(this),
								style: BUTTON_STYLE.FILLED,
								color: BUTTON_COLOR.WHITE,
						  },
					{
						text: this.state.addProjectPeopleMode
							? formatMessage({id: 'common.add'})
							: formatMessage({id: 'common.assign'}),
						disabled: this.state.addProjectPeopleMode && !this.selectedTeamMembersToShow().length,
						cy: 'assign-allocation-button',
						callback: this.addPeopleToProject.bind(this),
						style: BUTTON_STYLE.FILLED,
						color: BUTTON_COLOR.GREEN,
						preventDefaultClose: true,
					},
			  ]
			: [
					{
						text: formatMessage({id: 'common.cancel'}),
						callback: this.props.closeModal,
						style: BUTTON_STYLE.FILLED,
						color: BUTTON_COLOR.WHITE,
					},
					{
						text: formatMessage({id: 'new_project_modal.save_button'}),
						cy: 'create-project-button',
						callback: this.createProject.bind(this),
						style: BUTTON_STYLE.FILLED,
						color: BUTTON_COLOR.GREEN,
						preventDefaultClose: true,
						disabled: !datesValid || this.state.creatingProject,
					},
			  ];
		if (this.state.addTeamMembersStep) {
			if (this.state.showInviteTeamMemberForm) {
				buttons.unshift({
					text: formatMessage({id: 'common.go_back'}),
					cy: 'back-allocation-button',
					callback: this.goBack.bind(this),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.GREY,
					preventDefaultClose: true,
				});
			}
			const headerText = this.state.showInviteTeamMemberForm
				? formatMessage({id: 'new_project_modal.invite_team_members_title'})
				: this.state.addProjectPeopleMode
				? formatMessage({id: 'scheduling.add_people'})
				: formatMessage({id: 'common.assign'});
			return (
				<GenericModal
					useScrollbars={false}
					closeModal={this.props.closeModal}
					headerText={headerText}
					buttons={buttons}
					content={this.getV3AddPeopleToProjectContent()}
				/>
			);
		}
		if (this.state.addAutoScheduleStep) {
			const buttons = [
				{
					text: formatMessage({id: 'common.skip'}),
					cy: 'skip-auto-scheduling-button',
					callback: this.endProjectDuplication.bind(this),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: formatMessage({id: 'auto_scheduling.auto_schedule'}),
					cy: 'auto-scheduling-button',
					className: 'auto-scheduling-button',
					callback: () => {
						tracking.trackElementClicked('Auto Schedule project from new project modal');
						trackEvent('Auto Schedule', 'Opened');
						this.openAutoScheduling();
					},
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.AI_BLUE,
					autoScheduleStyle: true,
				},
			];
			return (
				<GenericModal
					useScrollbars={false}
					closeModal={this.props.closeModal}
					headerText={formatMessage({id: 'auto_scheduling.auto_schedule'})}
					buttons={buttons}
					content={this.getAutoScheduleModal()}
					coverImage={<Nova02Image />}
					className="auto-scheduling-step"
				/>
			);
		}

		return (
			<GenericModal
				ref={e => (this.newProjectModal = e)}
				useScrollbars={true}
				closeModal={this.props.closeModal}
				headerText={formatMessage({id: 'new_project.create_new_project'})}
				buttons={buttons}
				content={this.getNewProjectModalContent()}
			/>
		);
	}
}

const newProjectModalV2Query = graphql`
	query newProjectModalV2_Query($projectId: ID, $fetchProject: Boolean!) {
		viewer {
			actualPersonId
			component(name: "new_project_modal")
			...newProjectModalV2_viewer @arguments(projectId: $projectId, fetchProject: $fetchProject)
		}
	}
`;

// The unwrapped component, for tests that render it without the Relay and router wrappers.
export {newProjectModalV2, newProjectModalV2Query};

export default injectIntl(
	withRouter(
		createFragmentContainer(newProjectModalV2, {
			viewer: graphql`
				fragment newProjectModalV2_viewer on Viewer
				@argumentDefinitions(projectId: {type: "ID"}, fetchProject: {type: "Boolean!"}) {
					language
					id
					actualPersonId
					availableFeatureFlags {
						key
					}
					project(internalId: $projectId) @include(if: $fetchProject) {
						id
						projectGroupId
						projectPersons(first: 1000) {
							edges {
								node {
									id
									person {
										id
									}
								}
							}
						}
					}
					company {
						id
						modules {
							moduleType
						}
						profiles(first: 10000) @connection(key: "Company_profiles", filters: []) {
							edges {
								node {
									id
									name
									canBeEdited
									personsCount
									permissions
									disabled
								}
							}
						}
						currency
						createdAt
						userSeats
						virtualSeats
						tier
						isChargebeeCustomer
						isUsingProjectAllocation
						isUsingMixedAllocation
						oktaEnabled
						sageIntacctEnabled
						sageIntacctConnectionEnabled
						sageLocationEntities {
							locationId
							name
							isPrimaryConnection
						}
						oneloginEnabled
						AADEnabled
						useTimeApproval
						projectLastSequence
						labels(first: 10000, labelType: PROJECT) {
							edges {
								node {
									id
									name
									color
									category {
										id
										name
									}
								}
							}
						}
						clients(first: 10000) @connection(key: "Company_clients") {
							edges {
								node {
									id
									name
									sageIntacctLocationEntityId
								}
							}
						}
						allPersons(first: 10000, onlyActive: true) @connection(key: "Company_modal_allPersons", filters: []) {
							edges {
								node {
									id
									active
									role {
										id
										name
									}
									fullName
									firstName
									lastName
									email
									permissions
									client {
										id
									}
									profilePictureId
									profilePictureDefaultId
								}
							}
						}
						teams(first: 1000) {
							edges {
								node {
									id
									name
									teamPersons(first: 1000) {
										edges {
											node {
												id
												person {
													id
												}
											}
										}
									}
								}
							}
						}
						roles(first: 1000) {
							edges {
								node {
									id
									name
								}
							}
						}
						defaultRateCard {
							id
							name
							currency
						}
						rateCards(first: 10000) {
							edges {
								node {
									id
									name
									defaultRate
									currency
									parentRateCardId
									disabled
								}
							}
						}
					}
				}
			`,
		})
	)
);
